import { isoDay, type Counts, type PermissionItem, type Task, type TaskEvent, type WsEvent } from '@jerome2/shared';
import { api } from '../api.ts';
import { createStore } from '../lib/store.ts';

// Taken (wachtrij), tellers en akkoordvragen. Gevuld bij het starten en via de websocket.

export interface TasksState {
  byId: Record<string, Task>;
  events: Record<string, TaskEvent[]>;
  counts: Counts;
  /** Zijn de tellers van de server er al (GET /api/mail/counts of de melding `counts`)? Tot dan zijn ze nul, niet echt. */
  countsLoaded: boolean;
  permissions: PermissionItem[];
  loaded: boolean;
}

export const tasksStore = createStore<TasksState>({ byId: {}, events: {}, counts: { flagged: 0, unread: 0, done: 0, question: 0, running: 0, failed: 0 }, countsLoaded: false, permissions: [], loaded: false });

export const ACTIVE_STATUSES = new Set(['queued', 'running', 'question', 'done', 'failed']);
export const BUSY_STATUSES = new Set(['queued', 'running']);
/** Taken die op Jeroen wachten: klaar met resultaat, of met een vraag. Die staan in Gevlagd/Ongelezen bovenaan onder "Klaar voor jou". */
export const READY_STATUSES = new Set(['done', 'question']);
/** Taken waardoor een mail uit Gevlagd/Ongelezen verdwijnt (hij staat dan bij Verwerkt). */
export const HIDING_STATUSES = new Set(['queued', 'running', 'question', 'done']);

/** Nooit een oudere stand over een nieuwere heen (een laat websocketbericht na een vers antwoord van de server). */
function newer(cur: Task | undefined, t: Task): boolean {
  return !cur || t.updatedAt >= cur.updatedAt;
}

export function upsertTask(task: Task): void {
  tasksStore.set((s) => (newer(s.byId[task.id], task) ? { ...s, byId: { ...s.byId, [task.id]: task } } : s));
}

export function upsertTasks(list: Task[]): void {
  tasksStore.set((s) => {
    const byId = { ...s.byId };
    for (const t of list) if (newer(byId[t.id], t)) byId[t.id] = t;
    return { ...s, byId, loaded: true };
  });
}

export async function loadActiveTasks(): Promise<void> {
  // De akkoordvragen tegelijk met de taken: een mislukte vraag ernaar houdt de rest niet op.
  const [active, today, p] = await Promise.all([api<{ tasks: Task[] }>('/api/tasks?status=active'), api<{ tasks: Task[] }>(`/api/tasks?day=${isoDay(new Date())}`), api<{ permissions: PermissionItem[] }>('/api/tasks/permissions').catch(() => ({ permissions: [] as PermissionItem[] }))]);
  const ids = new Set([...active.tasks, ...today.tasks].map((t) => t.id));
  tasksStore.set((s) => {
    const byId: Record<string, Task> = {};
    for (const t of Object.values(s.byId)) if (!ACTIVE_STATUSES.has(t.status) || ids.has(t.id)) byId[t.id] = t;
    for (const t of [...active.tasks, ...today.tasks]) if (newer(byId[t.id], t)) byId[t.id] = t;
    return { ...s, byId, loaded: true, permissions: p.permissions };
  });
}

export async function loadTask(id: string): Promise<{ task: Task; events: TaskEvent[] }> {
  const r = await api<{ task: Task; events: TaskEvent[] }>(`/api/tasks/${encodeURIComponent(id)}`);
  tasksStore.set((s) => ({ ...s, byId: { ...s.byId, [r.task.id]: r.task }, events: { ...s.events, [r.task.id]: r.events } }));
  return r;
}

export async function loadCounts(opts: { background?: boolean } = {}): Promise<void> {
  const counts = await api<Counts>('/api/mail/counts', { background: opts.background ?? false });
  tasksStore.patch({ counts, countsLoaded: true });
}

export function applyTaskEvent(ev: WsEvent): void {
  if (ev.type === 'task.updated') upsertTask(ev.task);
  else if (ev.type === 'task.event') {
    tasksStore.set((s) => {
      const list = s.events[ev.event.taskId];
      if (!list) return s;
      if (list.some((e) => e.id === ev.event.id)) return s;
      return { ...s, events: { ...s.events, [ev.event.taskId]: [...list, ev.event] } };
    });
  } else if (ev.type === 'counts') tasksStore.patch({ counts: ev.counts, countsLoaded: true });
  else if (ev.type === 'permission') {
    tasksStore.set((s) => {
      const rest = s.permissions.filter((p) => p.id !== ev.permission.id);
      return { ...s, permissions: ev.permission.status === 'pending' ? [...rest, ev.permission] : rest };
    });
  }
}

const post = (id: string, action: string, body?: unknown) => api<Task>(`/api/tasks/${encodeURIComponent(id)}/${action}`, { method: 'POST', ...(body !== undefined ? { body } : {}) });

export const taskActions = {
  async answer(id: string, text: string) {
    upsertTask(await post(id, 'answer', { text }));
  },
  async steer(id: string, text: string) {
    upsertTask(await post(id, 'steer', { text }));
  },
  async retry(id: string) {
    upsertTask(await post(id, 'retry'));
  },
  async cancel(id: string) {
    upsertTask(await post(id, 'cancel'));
  },
  async finish(id: string) {
    upsertTask(await post(id, 'finish'));
  },
  async feedback(id: string, text: string) {
    await post(id, 'feedback', { text });
  },
  /** "Er klopt iets niet" bij een ondertekening: leerregel bewaren en de taak opnieuw laten lopen. */
  async signFeedback(id: string, text: string): Promise<{ text: string }> {
    const r = await api<{ task: Task; rule: { text: string } }>(`/api/sign/feedback/${encodeURIComponent(id)}`, { method: 'POST', body: { text } });
    upsertTask(r.task);
    return r.rule;
  },
  async approve(id: string) {
    const r = await api<{ ok: boolean; task: Task }>(`/api/tasks/${encodeURIComponent(id)}/approve`, { method: 'POST' });
    upsertTask(r.task);
  },
  async decidePermission(id: string, allow: boolean) {
    await api(`/api/tasks/permissions/${encodeURIComponent(id)}`, { method: 'POST', body: { allow } });
    tasksStore.set((s) => ({ ...s, permissions: s.permissions.filter((p) => p.id !== id) }));
  },
};

/** Volgorde van taken waar er één gekozen of bovenaan gezet wordt: bezig, wachtend, vraag, klaar, mislukt, afgerond, geannuleerd. */
const STATUS_RANK: Record<string, number> = { running: 0, queued: 1, question: 2, done: 3, failed: 4, finished: 5, cancelled: 6 };

export function selectTasks(state: TasksState, opts: { showAll: boolean; now?: Date }): Task[] {
  const today = isoDay(opts.now ?? new Date());
  const list = Object.values(state.byId).filter((t) => (opts.showAll ? ACTIVE_STATUSES.has(t.status) || t.updatedAt.startsWith(today) : BUSY_STATUSES.has(t.status)));
  return list.sort((a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9) || b.updatedAt.localeCompare(a.updatedAt));
}

/** De taak die bij een mail hoort als er maar één getoond wordt: dezelfde rang als de server (`latestOf`), daarbinnen de nieuwste. */
export function latestTaskForMessage(state: TasksState, messageId: string): Task | null {
  let best: Task | null = null;
  const rank = (t: Task) => STATUS_RANK[t.status] ?? 9;
  for (const t of Object.values(state.byId)) {
    if (t.messageId !== messageId) continue;
    if (!best || rank(t) < rank(best) || (rank(t) === rank(best) && t.createdAt > best.createdAt)) best = t;
  }
  return best;
}

/** Andere open taken van dezelfde mail; Klaar haalt de vlag pas weg bij de laatste. */
export function openSiblings(state: TasksState, messageId: string, excludeId: string): number {
  let n = 0;
  for (const t of Object.values(state.byId)) if (t.messageId === messageId && t.id !== excludeId && ACTIVE_STATUSES.has(t.status)) n += 1;
  return n;
}
